/**
 * Phase K.5 (2026-08-07) — Public invoice view.
 *
 * Route: /public/invoices/:token (no auth guard)
 *
 * Mirrors ``PublicStatementView.jsx`` but for a single invoice. See
 * that file's header for the design rationale (no login redirect,
 * friendly error UX on invalid/expired/rate-limited tokens).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { formatApiError, BACKEND_URL } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import PublicPayModal from "@/components/PublicPayModal";
import CreditMemosCard from "@/components/CreditMemosCard";
import {
    Download, CreditCard, AlertTriangle, CheckCircle2, Clock,
    Loader2, FileText, ExternalLink, LogIn,
} from "lucide-react";

const STATUS_LABEL = {
    sent: "Sent", viewed: "Viewed", overdue: "Overdue",
    paid: "Paid", partially_paid: "Partially paid",
    processing: "Processing",
};
const STATUS_COLOR = {
    sent: "bg-blue-50 text-blue-900 ring-blue-200",
    viewed: "bg-slate-100 text-slate-800 ring-slate-200",
    overdue: "bg-red-50 text-red-700 ring-red-200",
    processing: "bg-amber-50 text-amber-900 ring-amber-200",
    partially_paid: "bg-amber-50 text-amber-900 ring-amber-200",
    paid: "bg-emerald-100 text-emerald-900 ring-emerald-200",
};

const PAYABLE = new Set(["sent", "viewed", "overdue"]);
const POLL_INTERVAL_MS = 5000;
const POLL_MAX = 12;

function fmtMoney(cents) {
    if (cents == null) return "—";
    const sign = cents < 0 ? "-" : "";
    const v = Math.abs(cents) / 100;
    return `${sign}$${v.toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
}
function fmtDate(s) { if (!s) return "—"; return String(s).slice(0, 10); }
function fmtDateTime(s) {
    if (!s) return "—";
    try { return new Date(s).toLocaleString(); } catch { return s; }
}

export default function PublicInvoiceView() {
    const { token } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [state, setState] = useState({
        loading: true, error: null, invoice: null,
        portalUrl: null, scope: null, hasActiveMandate: false,
    });
    const [payOpen, setPayOpen] = useState(false);
    const [postReturn, setPostReturn] = useState(false);
    const pollCountRef = useRef(0);

    const load = useCallback(async () => {
        try {
            const { data } = await publicApi.get(
                `/public/invoices/token/${token}`,
            );
            setState((st) => ({
                ...st,
                loading: false, error: null,
                invoice: data.invoice, portalUrl: data.portal_url,
                scope: data.scope,
                hasActiveMandate: !!data.has_active_mandate,
            }));
            return data.invoice;
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            setState((st) => ({
                ...st,
                loading: false,
                error: {
                    status,
                    code: detail?.code || (status === 429 ? "rate_limited" : "invalid_or_expired_token"),
                    message: detail?.message || formatApiError(err),
                    portalUrl: detail?.portal_url || `${BACKEND_URL}/login`,
                },
                invoice: null,
            }));
            return null;
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (searchParams.get("pay_return") === "1") {
            setPostReturn(true);
            const next = new URLSearchParams(searchParams);
            next.delete("pay_return");
            setSearchParams(next, { replace: true });
        }
    }, []); // eslint-disable-line

    useEffect(() => {
        if (!postReturn || !state.invoice) return;
        if (["paid", "partially_paid"].includes(state.invoice.status)) {
            setPostReturn(false);
            pollCountRef.current = 0;
            return;
        }
        if (pollCountRef.current >= POLL_MAX) {
            setPostReturn(false);
            return;
        }
        const t = setTimeout(() => {
            pollCountRef.current += 1;
            load();
        }, POLL_INTERVAL_MS);
        return () => clearTimeout(t);
    }, [postReturn, state.invoice, load]);

    const handleDownloadPdf = async () => {
        try {
            const res = await publicApi.get(
                `/public/invoices/token/${token}/pdf?download=true`,
                { responseType: "blob" },
            );
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const inv = state.invoice;
            const short = inv?.invoice_number || (inv?.id ? inv.id.slice(0, 8) : "invoice");
            a.download = `invoice-${short}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        } catch (err) {
            alert(formatApiError(err));
        }
    };

    if (state.loading) {
        return (
            <PublicShell>
                <div className="py-16 text-center text-slate-500"
                     data-testid="public-invoice-loading">
                    <Loader2 className="h-6 w-6 animate-spin inline mr-2" />
                    Loading…
                </div>
            </PublicShell>
        );
    }

    if (state.error) {
        const isRateLimit = state.error.status === 429 || state.error.code === "rate_limited";
        const title = isRateLimit
            ? "Too many views on this link"
            : "This link is no longer valid";
        const body = isRateLimit
            ? "Please sign in to your customer portal to continue viewing this invoice."
            : "For your security, this link may have expired or been revoked. Sign in to your customer portal to view or pay the invoice.";
        return (
            <PublicShell>
                <div className="mx-auto max-w-md py-16" data-testid="public-invoice-error">
                    <Card>
                        <CardContent className="p-6 text-center">
                            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                                <AlertTriangle className="h-6 w-6 text-amber-700" />
                            </div>
                            <h1 className="text-lg font-semibold text-slate-900 mb-2">
                                {title}
                            </h1>
                            <p className="text-sm text-slate-600 mb-6">
                                {body}
                            </p>
                            <a
                                href={state.error.portalUrl}
                                data-testid="public-invoice-signin-cta"
                            >
                                <Button className="bg-blue-900 hover:bg-blue-800 gap-2">
                                    <LogIn className="h-4 w-4" />
                                    Sign in to view
                                </Button>
                            </a>
                        </CardContent>
                    </Card>
                </div>
            </PublicShell>
        );
    }

    const inv = state.invoice;
    const netDue = Math.max(
        0, Number(inv.total_cents || 0) - Number(inv.refunded_amount_cents || 0),
    );
    const canPay = PAYABLE.has(inv.status) && state.scope === "view_and_pay" && netDue > 0;
    const returnUrl = `${window.location.origin}/public/invoices/${token}?pay_return=1`;

    return (
        <PublicShell>
            <div className="mx-auto max-w-4xl px-4 py-8"
                 data-testid="public-invoice-view">
                <Card className="mb-6">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between gap-4 mb-4">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <h1 className="text-xl font-semibold text-slate-900"
                                        data-testid="public-invoice-header">
                                        Invoice {inv.invoice_number || (inv.id || "").slice(0, 8)}
                                    </h1>
                                    <span
                                        className={`text-xs px-2 py-0.5 rounded ring-1 ${
                                            STATUS_COLOR[inv.status] || "bg-slate-100"
                                        }`}
                                        data-testid="public-invoice-status"
                                    >
                                        {STATUS_LABEL[inv.status] || inv.status}
                                    </span>
                                </div>
                                {inv.customer_name && (
                                    <div className="text-sm text-slate-600">
                                        {inv.customer_name}
                                    </div>
                                )}
                                <div className="text-sm text-slate-500">
                                    Due {fmtDate(inv.due_date)}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-xs uppercase tracking-wider text-slate-500">
                                    Total
                                </div>
                                <div className="text-3xl font-semibold text-slate-900 mt-0.5"
                                     data-testid="public-invoice-total">
                                    {fmtMoney(inv.total_cents)}
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {canPay && (
                                <Button
                                    onClick={() => setPayOpen(true)}
                                    className="gap-2 bg-blue-900 hover:bg-blue-800"
                                    data-testid="public-invoice-pay-btn"
                                >
                                    <CreditCard className="h-4 w-4" />
                                    Pay {fmtMoney(inv.total_cents)}
                                </Button>
                            )}
                            <Button
                                variant="outline"
                                onClick={handleDownloadPdf}
                                className="gap-2"
                                data-testid="public-invoice-pdf-btn"
                            >
                                <Download className="h-4 w-4" />
                                Download PDF
                            </Button>
                            {state.portalUrl && (
                                <a
                                    href={state.portalUrl}
                                    className="inline-flex"
                                    data-testid="public-invoice-portal-link"
                                >
                                    <Button variant="ghost" className="gap-2 text-blue-900">
                                        <ExternalLink className="h-4 w-4" />
                                        View in portal
                                    </Button>
                                </a>
                            )}
                        </div>

                        {postReturn && (
                            <div className="mt-4 rounded-md bg-amber-50 ring-1 ring-amber-200 p-3 text-sm text-amber-900 flex items-start gap-2"
                                 data-testid="public-invoice-processing-banner">
                                <Clock className="h-4 w-4 mt-0.5" />
                                <div>
                                    <div className="font-medium">Payment authorized — settling now</div>
                                    <div className="text-xs mt-0.5">
                                        ACH debits typically settle in 3–5 business days.
                                    </div>
                                </div>
                            </div>
                        )}
                        {inv.status === "paid" && (
                            <div className="mt-4 rounded-md bg-emerald-50 ring-1 ring-emerald-200 p-3 text-sm text-emerald-900 flex items-start gap-2"
                                 data-testid="public-invoice-paid-banner">
                                <CheckCircle2 className="h-4 w-4 mt-0.5" />
                                <div>
                                    <div className="font-medium">Invoice paid in full</div>
                                    <div className="text-xs mt-0.5">
                                        Settled at {fmtDateTime(inv.paid_at)}
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                            <FileText className="h-4 w-4 text-slate-500" />
                            <h2 className="text-sm font-semibold text-slate-900">
                                Line items ({(inv.line_items || []).length})
                            </h2>
                        </div>
                        {(!inv.line_items || inv.line_items.length === 0) ? (
                            <div className="text-sm text-slate-500">
                                No line items on this invoice.
                            </div>
                        ) : (
                            <>
                                {/* Mobile: stacked card list (Phase K.5.1) */}
                                <div className="space-y-2 sm:hidden"
                                     data-testid="public-invoice-lines-mobile">
                                    {(inv.line_items || []).map((row, idx) => (
                                        <div key={idx}
                                             className="border rounded-md p-3 bg-slate-50"
                                             data-testid="public-invoice-line-row">
                                            <div className="text-sm font-medium text-slate-900 mb-1 break-words">
                                                {row.description || "—"}
                                            </div>
                                            <div className="flex items-center justify-between text-xs text-slate-600">
                                                <span>
                                                    Qty {row.quantity ?? "—"}
                                                    <span className="mx-1 text-slate-400">·</span>
                                                    {fmtMoney(row.unit_amount_cents)} each
                                                </span>
                                                <span className="font-semibold text-slate-900">
                                                    {fmtMoney(
                                                        (row.quantity || 0) *
                                                            (row.unit_amount_cents || 0),
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {/* Desktop/tablet: full table */}
                                <div className="hidden sm:block border rounded-md overflow-hidden">
                                    <table className="w-full text-sm">
                                        <thead className="bg-slate-50 text-slate-600">
                                            <tr>
                                                <th className="text-left px-3 py-2">Description</th>
                                                <th className="text-right px-3 py-2">Qty</th>
                                                <th className="text-right px-3 py-2">Unit</th>
                                                <th className="text-right px-3 py-2">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {(inv.line_items || []).map((row, idx) => (
                                                <tr key={idx}
                                                    data-testid="public-invoice-line-row">
                                                    <td className="px-3 py-2">
                                                        {row.description || "—"}
                                                    </td>
                                                    <td className="px-3 py-2 text-right">
                                                        {row.quantity ?? "—"}
                                                    </td>
                                                    <td className="px-3 py-2 text-right">
                                                        {fmtMoney(row.unit_amount_cents)}
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-medium">
                                                        {fmtMoney(
                                                            (row.quantity || 0) *
                                                                (row.unit_amount_cents || 0),
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Phase K.4 (2026-08-07) — Credit memos card. */}
                <CreditMemosCard
                    memos={inv.credit_memos}
                    paidCents={inv.total_cents}
                    refundedCents={inv.refunded_amount_cents}
                />
            </div>

            <PublicPayModal
                kind="invoice"
                token={token}
                amountCents={inv.total_cents}
                label={`Invoice ${inv.invoice_number || (inv.id || "").slice(0, 8)}`}
                returnUrl={returnUrl}
                open={payOpen}
                onOpenChange={setPayOpen}
                onInitiated={() => setPostReturn(true)}
                hasActiveMandate={state.hasActiveMandate}
            />
        </PublicShell>
    );
}

function PublicShell({ children }) {
    return (
        <div className="min-h-screen bg-slate-50">
            <div className="border-b bg-white">
                <div className="mx-auto max-w-4xl px-4 py-3 flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900 tracking-tight">
                        Secure Invoice Portal
                    </div>
                    <div className="text-xs text-slate-500">
                        Powered by ACH via Stripe
                    </div>
                </div>
            </div>
            {children}
        </div>
    );
}
